import { updateWaitDeviceStatus } from "../shared/hitl/actionPause";
//src/reducers/chatReducer.ts
import { ActionTypes } from "store/actionTypes";
import ChatState from "models/state/chatState";
import Action from "store/action";
import {
  ChatMessage,
  extractHumanReviewPayloadFromContent,
  normalizeMessages,
} from "shared/utils/messageUtils";

type RemoteShellSlot = {
  remoteSession: any;
  sshEvents: any[];
};

const emptyShellSlot = (): RemoteShellSlot => ({
  remoteSession: null,
  sshEvents: [],
});

const cloneShellMap = (map: Record<string, RemoteShellSlot> | undefined) =>
  map ? { ...map } : {};

const deleteShellKey = (
  map: Record<string, RemoteShellSlot>,
  threadId: any
): Record<string, RemoteShellSlot> => {
  const key = String(threadId);
  if (!map[key]) return map;
  const next = { ...map };
  delete next[key];
  return next;
};

/** One recording: the video of a single browser session inside a run. */
export type ComputerUseRecordingState = {
  session_id: string;
  object_name: string;
  video_url: string | null;
  thumbnail_url: string | null;
  /**
   * The job that produced this video, which is NOT always the run's own `jobId`.
   *
   * A run is keyed by the tool call, and one tool call can span several jobs: a HITL pause is
   * resumed under the same call, so the answer, the retry and the work that follows are separate
   * jobs sharing one call id. The run adopts the FIRST job it sees, so a recording banked by a
   * later job was being signed against the wrong one -- the lookup filters on job id, found
   * nothing, and the player sat on a 404 for a video that existed.
   */
  job_id?: number | null;
  /**
   * When the browser this recorded was opened.
   *
   * A run can hold several sessions -- a failed attempt, a sign-in, the retry -- and they
   * happen at different points in the conversation, with the technician's own decisions in
   * between. This is what lets each one be drawn where it belongs instead of all of them
   * together under the tool call that started the first.
   */
  started_at: string | null;
};

/**
 * One browser run as the chat knows it.
 *
 * `status` follows the job's own vocabulary, with one addition the job does not have:
 * `preparing_recording`. The run's work is finished by then but the panel is not, because the
 * provider is still making the video, and a technician staring at a closed browser needs to be
 * told that rather than left guessing.
 */
export type ComputerUseRunState = {
  jobId: number | null;
  callId: string;
  sessionId: string | null;
  status: string;
  browserUrl: string | null;
  /**
   * Has the browser got anything on screen yet?
   *
   * A session exists a good while before it does: the URL is handed over as soon as the
   * provider returns one, which is before the script has been spawned and long before it has
   * navigated, and the live view of an empty tab is black. Only the script can say when that
   * stops being true.
   */
  browserReady: boolean;
  recordings: ComputerUseRecordingState[];
  startedAt: string | null;
  /**
   * When each of this run's browsers was opened, by session id.
   *
   * Kept because the two facts arrive apart: a session announces itself when it opens, and its
   * recording arrives minutes later carrying only the session id. Without this the recording
   * has no time of its own and falls back to the run's, which puts every one of them in the
   * same place.
   */
  sessionStartedAt: Record<string, string>;
  /**
   * Did this run stop because a person has to do something, rather than because it broke?
   *
   * The job settles as an error either way, so the status cannot tell them apart. Cortex can --
   * the mapping from what went wrong to what happens next lives in one table there -- and it
   * says so on the event that ends the run.
   */
  needsTechnician: boolean;
  recordingError: string | null;
  // Whether this run had a session deliberately left unrecorded. The panel waits for a video
  // after every close, so it needs to be told when one is not coming.
  recordingSkipped: boolean;
  /**
   * Did this run only read?
   *
   * A read opens a browser and looks at something; nothing in the tenant is different
   * afterwards. There is usually no live view worth watching and no recording coming, so the
   * panel draws nothing at all -- except while the run is parked at a sign-in, where the
   * browser is the technician's to type into and has to be on screen.
   *
   * Not every read, though, and the name is older than the rule. What Cortex now answers here
   * is "should this run's browser be kept off the thread entirely", which is not the same
   * question as "did anything change". A 1Password read changes nothing and still has to be
   * watched, because Theo holds no 1Password password: the technician signs in to the browser
   * on screen, so it stays on screen until the run is done rather than vanishing the instant
   * they finish. See `hides_panel` in Cortex's computer-use registry.
   *
   * Cortex decides it either way. Nothing on the job row or in the event stream would otherwise
   * distinguish a run whose browser is worth showing from one whose is not.
   */
  readOnly: boolean;
  /**
   * Was this run one Theo took on its own account?
   *
   * A long transfer is checked on repeatedly, and each check opens a browser. Drawing a panel
   * for every one would bury the conversation under dozens of browsers nobody asked for and
   * nobody watched -- so these draw nothing, the same way a read does, and for the same reason:
   * there is nothing on screen worth a technician's attention.
   *
   * Cortex decides it, per script, and says so on the event that opens each session.
   */
  silent: boolean;
  /**
   * One short line saying what this run was about and how it went.
   *
   * The header over a recording used to read "Browser session recording", which tells somebody
   * opening a thread months later nothing at all -- they had to watch the video to find out
   * what it was of. This is written by the script that did the work, so it is what actually
   * happened rather than a paraphrase.
   *
   * Empty when the run never said one, and the generic labels stand in.
   */
  title: string;
  /**
   * Which try this run is on, once it is past the first.
   *
   * Null for the overwhelming majority of runs, which work first time. A retry restarts the
   * script in the browser the technician is watching, so the page they are looking at resets
   * itself for no reason they can see -- this is what lets the panel say why.
   */
  retry: { tryNumber: number; maxTries: number } | null;
};

/** A thread's runs, keyed so that repeated events fold into the run they belong to. */
type ComputerUseSlot = {
  runs: Record<string, ComputerUseRunState>;
};

/**
 * Which run an event belongs to.
 *
 * call_id first: it is the only key stable across a rescue, which closes one session and opens
 * another under the same run. Falling back to job or session id keeps an event that predates
 * the anchor from being dropped on the floor.
 */
export const computerUseRunKey = (event: any): string | null => {
  const callId = String(event?.call_id || "").trim();
  if (callId) return callId;
  if (event?.job_id != null) return `job-${event.job_id}`;
  const sessionId = String(event?.session_id || "").trim();
  return sessionId ? `session-${sessionId}` : null;
};

/**
 * Where an event lands among the runs already on screen.
 *
 * A run is keyed by the tool call that opened it, but not everything that can end a run knows
 * that call. Stop reaches the browser through the job row -- it is the one control that works
 * on a run with no turn streaming behind it -- so the cancellation it sends back can name only
 * the job. Keying that off `job-<id>` would open a *second*, empty run reading "cancelled"
 * beside the original still reading "live": two panels for one browser, neither right.
 *
 * The job is a real identity for a run, just not the one used for the key, so an event naming
 * a job belongs to whichever run has adopted it. Exact, not a guess.
 */
export const resolveComputerUseRunKey = (
  runs: Record<string, ComputerUseRunState>,
  event: any
): string | null => {
  const key = computerUseRunKey(event);
  if (key && runs[key]) return key;
  if (event?.job_id != null) {
    const jobId = Number(event.job_id);
    const owner = Object.keys(runs).find((candidate) => runs[candidate]?.jobId === jobId);
    if (owner) return owner;
  }
  return key;
};

const emptyRun = (key: string, event: any): ComputerUseRunState => ({
  jobId: event?.job_id != null ? Number(event.job_id) : null,
  callId: String(event?.call_id || key),
  sessionId: null,
  status: "executing",
  browserUrl: null,
  browserReady: false,
  recordings: [],
  startedAt: String(event?.timestamp || "") || null,
  sessionStartedAt: {},
  needsTechnician: false,
  recordingError: null,
  recordingSkipped: false,
  // Until a session opens and says otherwise. Defaulting the other way would hide the panel of
  // every run whose first event is still in flight.
  readOnly: false,
  // Until a session opens and says otherwise, for the same reason `readOnly` defaults this way:
  // defaulting to silent would hide the panel of every run whose first event is still in flight.
  silent: false,
  title: "",
  retry: null,
});

/**
 * Fold one lifecycle event into the run it belongs to.
 *
 * Every event type the runner emits is handled, including the ones with nothing to change:
 * an unhandled name silently doing nothing is indistinguishable from a bug, and this is the
 * only place the two vocabularies -- streamed and stored -- have to agree.
 */
export const applyComputerUseEvent = (
  incoming: ComputerUseRunState,
  event: any
): ComputerUseRunState => {
  const type = String(event?.event_type || "").toUpperCase();

  // Adopt the job the first time an event names it. The live stream is the only way a
  // watching client learns it -- the stored rows are not read until a reload -- and without
  // it the panel has nothing to ask for the recording with, so play does nothing at all.
  const run =
    incoming.jobId == null && event?.job_id != null
      ? { ...incoming, jobId: Number(event.job_id) }
      : incoming;

  switch (type) {
    case "SCRIPT_STARTED":
      // A script is beginning, so whatever the last one was on no longer describes what is
      // running. Clearing `retry` here is the whole point of the event arriving per script:
      // nothing else ever cleared it, and a run is keyed by the tool CALL -- which spans every
      // script of a session, the browser a HITL answer opens, and a resumed rescue. So one
      // retried script left "Theo is trying again (attempt 2 of 3)" over every first try that
      // followed it, in the same turn and in later ones.
      return { ...run, status: "executing", retry: null };

    case "SESSION_OPENING":
      return { ...run, status: "executing" };

    case "SESSION_OPENED": {
      // A rescue opens a second browser; the newest is the one worth watching. It starts
      // blank, like the first did, so readiness resets with it.
      const sessionId = String(event?.session_id || "");
      const openedAt = String(event?.timestamp || "");
      return {
        ...run,
        status: "executing",
        sessionId: sessionId || run.sessionId,
        browserUrl: String(event?.browser_url || "") || null,
        browserReady: false,
        readOnly: Boolean(event?.read_only),
        silent: Boolean(event?.silent),
        // Noted now because the recording that follows will name only this session, and by
        // then there is nothing left to say when it happened.
        sessionStartedAt: sessionId && openedAt ? { ...run.sessionStartedAt, [sessionId]: openedAt } : run.sessionStartedAt,
      };
    }

    case "PAGE_READY":
      return { ...run, browserReady: true };

    case "SCRIPT_RETRYING":
      // The script failed for a reason nothing recognised and is being run again in the same
      // browser.
      //
      // `browserReady` deliberately stays as it was. It answers "has this browser got anything
      // on screen yet", and the cover it drives exists for the black frame of a tab that has
      // never navigated. By the time a retry starts that is long past -- the page is right
      // there, and re-covering it hides a browser that is rendering perfectly well. Measured:
      // covering it again is the difference between watching the retry and watching a spinner.
      return {
        ...run,
        status: "executing",
        retry: { tryNumber: Number(event?.try_number || 0), maxTries: Number(event?.max_tries || 0) },
      };

    case "USER_INTERACTION_REQUIRED":
      return { ...run, status: "waiting_on_customer" };

    case "SESSION_OPEN_FAILED":
      return { ...run, status: "error", browserUrl: null };

    case "SESSION_CLOSING":
    case "SESSION_CLOSED":
      // The browser is gone. Keeping the URL would render an iframe against a dead page.
      return { ...run, browserUrl: null };

    case "RECORDING_PENDING":
      // The video is made after the session stops, so this follows a cancellation as readily as
      // it follows a clean finish. Only the status is withheld: relabelling a run the technician
      // stopped as one that finished would undo the thing they just did.
      return { ...run, status: run.status === "cancelled" ? run.status : "preparing_recording", browserUrl: null };

    case "RECORDING_READY": {
      const sessionId = String(event?.session_id || "");
      return {
        ...run,
        recordings: [
          ...run.recordings,
          {
            session_id: sessionId,
            object_name: String(event?.object_name || ""),
            // Stamped from the event, not from the run: see ComputerUseRecordingState.job_id.
            job_id: event?.job_id != null ? Number(event.job_id) : run.jobId,
            // The event names the objects; it cannot sign them. The player asks for its own
            // link, which is the only route open to a recording that arrived mid-stream.
            video_url: null,
            thumbnail_url: null,
            // The recording arrives when the session *stopped*, so its own timestamp would put
            // it after everything that happened while it was running. The session's opening is
            // when the thing it shows began.
            started_at: run.sessionStartedAt[sessionId] || null,
          },
        ],
      };
    }

    case "RECORDING_FAILED":
      return { ...run, recordingError: String(event?.error || "The recording could not be saved.") };

    case "RECORDING_SKIPPED":
      // Not a failure, and it must not be shown as one. This session was never going to be
      // recorded -- a run that only looked at a sign-in wall, or the browser the technician
      // signed in through -- so there is nothing to wait for and nothing to explain.
      return { ...run, recordingSkipped: true, browserUrl: null };

    case "JOB_CANCELLED":
      // Stop reaches the browser even when no turn is running, so this can arrive with the
      // run apparently mid-flight. It is terminal regardless.
      return { ...run, status: "cancelled", browserUrl: null };

    case "JOB_COMPLETED":
      // Cancelling kills the browser under a script that is still driving it, so that script
      // fails and reports itself failed. Cortex suppresses this event for a cancelled job, but
      // the guard lives here too: an older Cortex, or a replay of stored rows, would otherwise
      // turn a deliberate stop back into "Browser session failed".
      if (run.status === "cancelled") return { ...run, browserUrl: null };
      return {
        ...run,
        status: String(event?.status || "") === "success" ? "completed" : "error",
        // A run that stopped because a person has to do something settles as an error like any
        // other, so this is the only thing that separates the two afterwards.
        needsTechnician: Boolean(event?.interaction_key),
        // Kept if this event does not carry one, so a replay of older stored rows cannot blank
        // a title the live stream already delivered.
        title: String(event?.title || "") || run.title,
        browserUrl: null,
      };

    default:
      return run;
  }
};

/** Fields that are signed links: correct when minted, worthless once they age out. */
const SIGNED_RECORDING_FIELDS = ["video_url", "thumbnail_url"] as const;

/**
 * Take the freshly signed links, keep everything else already on screen.
 *
 * "Whatever is in the store wins" is right for a run's lifecycle -- an answer that was in
 * flight while the run moved on must not roll it backwards -- but wrong for these, which are
 * the one thing that goes stale by design. A link signed when the thread was first opened has
 * fifteen minutes on it; come back to that thread later and the merge would keep the dead link
 * in preference to the live one that just arrived, and nothing would ever replace it.
 *
 * Recordings the stream never saw are taken as they come: a client whose tab slept through a
 * RECORDING_READY has no other way to learn of them.
 */
const refreshRecordingLinks = (current: any[], stored: any[]): any[] => {
  const bySession = new Map((stored || []).map((rec) => [String(rec?.session_id || ""), rec]));
  const merged = (current || []).map((rec) => {
    const fresh = bySession.get(String(rec?.session_id || ""));
    if (!fresh) return rec;
    bySession.delete(String(rec?.session_id || ""));
    const links = Object.fromEntries(SIGNED_RECORDING_FIELDS.filter((f) => fresh[f]).map((f) => [f, fresh[f]]));
    return { ...rec, ...links };
  });
  return merged.concat(Array.from(bySession.values()));
};

const cloneComputerUseMap = (map: Record<string, ComputerUseSlot> | undefined) =>
  map ? { ...map } : {};

const deleteComputerUseKey = (
  map: Record<string, ComputerUseSlot>,
  threadId: any
): Record<string, ComputerUseSlot> => {
  const key = String(threadId);
  if (!map[key]) return map;
  const next = { ...map };
  delete next[key];
  return next;
};

const syncRemoteShellFromThread = (state: ChatState, thread: any): void => {
  const threadId = thread?.ai_thread_id;
  if (threadId == null) return;
  if (!Object.prototype.hasOwnProperty.call(thread, "remote_shell_session")) return;
  const session = thread?.remote_shell_session;
  const map = cloneShellMap(state.remoteShellByThreadId);
  if (!session || session.active !== true) {
    state.remoteShellByThreadId = deleteShellKey(map, threadId);
    return;
  }

  const threadKey = String(threadId);
  const prev = map[threadKey] || emptyShellSlot();
  const nextSession = {
    active: true,
    sessionId: session.session_id || null,
    deviceId: session.device_id || null,
    osType: session.os_type || null,
    shellType: session.shell_type || null,
    status: session.status || "connected",
  };
  const event = {
    event_type: "SESSION_OPENED",
    session_id: nextSession.sessionId,
    device_id: nextSession.deviceId,
    os_type: nextSession.osType,
    shell_type: nextSession.shellType,
    reason: "restored_after_reload",
    timestamp: new Date().toISOString(),
  };
  const alreadyActive =
    prev.remoteSession?.active === true &&
    String(prev.remoteSession?.sessionId || "") === String(nextSession.sessionId || "");
  map[threadKey] = {
    ...prev,
    remoteSession: nextSession,
    sshEvents: alreadyActive ? prev.sshEvents || [] : [...(prev.sshEvents || []), event],
  };
  state.remoteShellByThreadId = map;
};

const initialState = {
  isLoading: false,
  error: null,
  messages: [],
  aiThreadId: null,
  ticketId: null,
  userThreads: [],
  selectedUserThreadId: null,
  isThinking: false,
  thinkingSteps: [],
  currentThinkingStep: 0,
  planProgress: null,
  remoteShellByThreadId: {} as Record<string, RemoteShellSlot>,
  computerUseByThreadId: {} as Record<string, ComputerUseSlot>,
};

const sameThreadId = (a: any, b: any) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

function ticketIdFromPayload(ticketId: any): string | null {
  if (ticketId == null || ticketId === "") return null;
  if (typeof ticketId === "object" && ticketId.value != null && ticketId.value !== "") {
    return String(ticketId.value);
  }
  if (typeof ticketId === "string" || typeof ticketId === "number") {
    return String(ticketId);
  }
  return null;
}

function hasStreamingAssistant(messages: any[] | undefined): boolean {
  return (messages || []).some(
    (message: any) => message.user_id === "neo" && message.isStreaming === true
  );
}

function isActiveTurnInProgress(activeTurn: { status?: string } | null | undefined): boolean {
  const status = activeTurn?.status;
  return status === "accepted" || status === "queued" || status === "running";
}

function preserveInFlightThinking(state: ChatState, incomingThreadId: any): boolean {
  return (
    sameThreadId(incomingThreadId, state.aiThreadId) &&
    (Boolean(state.isThinking) || hasStreamingAssistant(state.messages))
  );
}

const getMessageEntryIds = (message: any): Set<string> => {
  const ids = new Set<string>();
  if (Array.isArray(message?.stream_entry_ids)) {
    message.stream_entry_ids.forEach((entryId: any) => {
      if (entryId != null && String(entryId).trim()) {
        ids.add(String(entryId));
      }
    });
  }
  if (message?.entry_id != null && String(message.entry_id).trim()) {
    ids.add(String(message.entry_id));
  }
  return ids;
};

const messageHasEntryId = (message: any, entryId: any): boolean => {
  if (entryId == null || !String(entryId).trim()) return false;
  return getMessageEntryIds(message).has(String(entryId));
};

const appendMessageEntryId = (existing: any, incoming: any): string[] | undefined => {
  const ids = getMessageEntryIds(existing);
  getMessageEntryIds(incoming).forEach((entryId) => ids.add(entryId));
  return ids.size > 0 ? Array.from(ids) : undefined;
};

const getSelectedThreadMessages = (state: ChatState): any[] => {
  if (!state.selectedUserThreadId) return [];
  return (
    (state.userThreads || []).find((thread: any) =>
      sameThreadId(thread.ai_thread_id, state.selectedUserThreadId)
    )?.messages || []
  );
};

const hasExistingEntryId = (messages: any[], message: any): boolean =>
  Boolean(message?.entry_id) &&
  (messages || []).some((existing) => messageHasEntryId(existing, message.entry_id));

const getStableMessageId = (message: any): string =>
  message?.message_id != null && !String(message.message_id).startsWith("temp_")
    ? String(message.message_id)
    : "";

const getCreatedAtKey = (message: any): string => {
  const createdAt = message?.created_at;
  if (!createdAt) return "";
  if (typeof createdAt === "string") return createdAt;
  const seconds = createdAt?.seconds ?? createdAt?.value?.seconds;
  const nanos = createdAt?.nanos ?? createdAt?.value?.nanos ?? 0;
  if (seconds == null) return "";
  return `${String(seconds)}:${String(nanos)}`;
};

const isSamePersistedMessage = (existing: any, incoming: any): boolean => {
  if (existing?.pending || incoming?.pending) return false;
  const existingMessageId = getStableMessageId(existing);
  const incomingMessageId = getStableMessageId(incoming);
  const sameContent =
    String(existing?.content_type || "MESSAGE") === String(incoming?.content_type || "MESSAGE") &&
    normalizeComparableContent(existing?.content) === normalizeComparableContent(incoming?.content);
  if (existingMessageId && incomingMessageId) {
    if (existingMessageId !== incomingMessageId) return false;
    const existingCreatedAt = getCreatedAtKey(existing);
    const incomingCreatedAt = getCreatedAtKey(incoming);
    return sameContent || Boolean(existingCreatedAt && incomingCreatedAt && existingCreatedAt === incomingCreatedAt);
  }

  const existingCreatedAt = getCreatedAtKey(existing);
  const incomingCreatedAt = getCreatedAtKey(incoming);
  if (!existingCreatedAt || !incomingCreatedAt || existingCreatedAt !== incomingCreatedAt) {
    return false;
  }

  return String(existing?.user_id || "") === String(incoming?.user_id || "") && sameContent;
};

const hasExistingPersistedMessage = (messages: any[], message: any): boolean =>
  (messages || []).some((existing) => isSamePersistedMessage(existing, message));

const normalizeComparableContent = (content: any): string =>
  typeof content === "string" ? content.trim().replace(/\s+/g, " ") : "";

const getRequestId = (message: any): string =>
  message?.requestId != null && String(message.requestId).trim()
    ? String(message.requestId).trim()
    : "";

const messageThreadMatches = (existing: any, incoming: any): boolean =>
  existing?.ai_thread_id == null ||
  incoming?.ai_thread_id == null ||
  sameThreadId(existing.ai_thread_id, incoming.ai_thread_id);

const pendingUserMessageMatches = (existing: any, incoming: any): boolean => {
  if (!existing?.pending) return false;
  if (String(existing?.user_id || "") !== String(incoming?.user_id || "")) {
    return false;
  }
  if (String(existing?.content_type || "MESSAGE") !== String(incoming?.content_type || "MESSAGE")) {
    return false;
  }
  if (!messageThreadMatches(existing, incoming)) return false;

  const existingRequestId = getRequestId(existing);
  const incomingRequestId = getRequestId(incoming);
  if (existingRequestId && incomingRequestId) {
    return existingRequestId === incomingRequestId;
  }
  if (!incomingRequestId && existingRequestId) {
    return false;
  }
  if (String(incoming?.content_type || "MESSAGE") === "EXPORT_REQUEST" && !incomingRequestId) {
    return false;
  }

  return normalizeComparableContent(existing?.content) === normalizeComparableContent(incoming?.content);
};

const findPendingMessageIndex = (messages: any[], message: any): number => {
  const requestId = getRequestId(message);
  if (requestId) {
    const requestMatch = (messages || []).findIndex(
      (m) => m?.pending && getRequestId(m) === requestId
    );
    if (requestMatch !== -1) return requestMatch;
  }

  return (messages || []).findIndex((m) => pendingUserMessageMatches(m, message));
};

/**
 * The server's copy of a message that was shown optimistically. The server copy wins, but the
 * agent the technician picked is kept when the server copy does not name one, so the message's
 * Auto Resolver badge keeps naming that agent.
 */
const confirmPendingMessage = (pending: any, incoming: any): any => {
  const confirmed = { ...incoming, pending: false };
  if (!confirmed.agent_id && pending?.agent_id) confirmed.agent_id = pending.agent_id;
  return confirmed;
};

const mergePendingMessage = (existing: any, incoming: any): any => {
  const merged = { ...existing, ...incoming };
  if ((incoming?.message_id == null || incoming.message_id === "") && existing?.message_id != null) {
    merged.message_id = existing.message_id;
  }
  if (!getRequestId(incoming) && getRequestId(existing)) {
    merged.requestId = existing.requestId;
  }
  return merged;
};

const updateSelectedThreadMessages = (
  state: ChatState,
  updater: (messages: any[]) => any[]
): void => {
  if (!state.selectedUserThreadId) return;
  state.userThreads = (state.userThreads || []).map((thread: any) =>
    sameThreadId(thread.ai_thread_id, state.selectedUserThreadId)
      ? { ...thread, messages: updater(thread.messages || []) }
      : thread
  );
};

const isDuplicateCompletedAssistantMessage = (existing: any, incoming: any): boolean => {
  if (!existing || !incoming) return false;
  if (incoming.user_id !== "neo" || incoming.isComplete !== true) return false;
  if (existing.user_id !== "neo" || existing.isStreaming === true) return false;
  if ((existing.content_type || "MESSAGE") !== (incoming.content_type || "MESSAGE")) {
    return false;
  }
  const existingContent = normalizeComparableContent(existing.content);
  const incomingContent = normalizeComparableContent(incoming.content);
  return Boolean(existingContent && incomingContent && existingContent === incomingContent);
};

const hasDuplicateCompletedAssistantMessage = (messages: any[], message: any): boolean => {
  if (message?.user_id !== "neo" || message?.isComplete !== true) return false;
  const lastMessage = (messages || [])[messages.length - 1];
  return isDuplicateCompletedAssistantMessage(lastMessage, message);
};

const hasReplayChunkForCompletedAssistantMessage = (messages: any[], message: any): boolean => {
  if (message?.user_id !== "neo" || message?.isStreaming !== true) return false;
  const lastMessage = (messages || [])[messages.length - 1];
  if (!lastMessage || lastMessage.user_id !== "neo" || lastMessage.isStreaming === true) {
    return false;
  }

  const incomingMessageId = getStableMessageId(message);
  const lastMessageId = getStableMessageId(lastMessage);
  if (incomingMessageId && lastMessageId && incomingMessageId === lastMessageId) {
    return true;
  }

  const incomingRequestId = String(message.requestId || "").trim();
  const lastRequestId = String(lastMessage.requestId || "").trim();
  return Boolean(incomingRequestId && lastRequestId && incomingRequestId === lastRequestId);
};

const reducer = (state: ChatState = initialState, action: Action) => {
  let newState: ChatState = { ...state };

  switch (action.type) {
    case ActionTypes.SET_THREAD_AUTOMATION: {
      const { aiThreadId, capacity } = action.payload;
      if (!aiThreadId || (capacity !== 0 && capacity !== 3)) return state;
      return {
        ...state,
        threadAutomationByThreadId: { ...state.threadAutomationByThreadId, [String(aiThreadId)]: capacity },
      };
    }
    case ActionTypes.CHAT_LOADING:
      // getTicketThread / createTicketThread dispatch CHAT_LOADING with no
      // payload to start a fetch. Treat missing/undefined as true so the
      // conversation pane can show a spinner instead of empty-state prompts.
      newState.isLoading = action.payload !== false;
      return newState;

    case ActionTypes.CHAT_ERROR:
      newState.isLoading = false;
      newState.isThinking = false;
      newState.thinkingSteps = [];
      newState.currentThinkingStep = 0;
      newState.error =
        typeof action.payload === "string"
          ? action.payload
          : action.payload?.message || "Something went wrong.";
      return newState;

    case ActionTypes.CREATE_TICKET_THREAD:
      newState.isLoading = false; // This is chatIsLoading - ensure it's false when thread is created
      newState.aiThreadId = action.payload?.ai_thread_id || newState.aiThreadId;
      if (newState.aiThreadId) {
        newState.threadAutomationByThreadId = { ...state.threadAutomationByThreadId, [String(newState.aiThreadId)]: 0 };
      }
      newState.messages = normalizeMessages(action.payload?.messages || []);
      const createdTicketId = ticketIdFromPayload(action.payload?.ticket_id);
      if (createdTicketId) {
        newState.ticketId = createdTicketId;
      }
      // Reset thinking/loading state when thread is created
      newState.isThinking = false;
      newState.thinkingSteps = [];
      newState.currentThinkingStep = 0;
      return newState;

    case ActionTypes.GET_TICKET_THREAD: {
      const ticketTurnActive = isActiveTurnInProgress(action.payload?.ai_thread?.active_turn);
      newState.isLoading = ticketTurnActive;
      newState.aiThreadId =
        action.payload?.ai_thread?.ai_thread_id || newState.aiThreadId;
      const incomingThreadId = newState.aiThreadId;
      const capacity = action.payload?.ai_thread?.automation_capacity;
      if (incomingThreadId && (capacity === 0 || capacity === 3)) {
        newState.threadAutomationByThreadId = { ...state.threadAutomationByThreadId, [String(incomingThreadId)]: capacity };
      }
      const isSameThread = sameThreadId(incomingThreadId, state.aiThreadId);
      const hasStreaming = hasStreamingAssistant(state.messages);
      if (!(isSameThread && hasStreaming)) {
        const rawMessages = action.payload?.ai_thread?.messages || [];
        newState.messages = normalizeMessages(rawMessages);
      }
      const loadedTicketId = ticketIdFromPayload(action.payload?.ai_thread?.ticket_id);
      if (loadedTicketId) {
        newState.ticketId = loadedTicketId;
      }
      syncRemoteShellFromThread(newState, action.payload?.ai_thread);
      if (ticketTurnActive || preserveInFlightThinking(state, incomingThreadId)) {
        newState.isLoading = true;
        newState.isThinking = true;
      } else {
        newState.isThinking = false;
        newState.thinkingSteps = [];
        newState.currentThinkingStep = 0;
      }
      return newState;
    }

    case ActionTypes.CREATE_USER_THREAD: {
      newState.isLoading = false; // This is chatIsLoading - ensure it's false when thread is created
      const payloadThread = action.payload?.ai_thread || action.payload || {};
      const payloadUserId =
        payloadThread?.user_id ?? action.payload?.user_id ?? null;
      const createdThreadId =
        payloadThread?.ai_thread_id || action.payload?.ai_thread_id || null;

      if (createdThreadId) {
        newState.aiThreadId = createdThreadId;
      }
      newState.messages = normalizeMessages(payloadThread?.messages || []);

      if (createdThreadId) {
        const exists = (newState.userThreads || []).some(
          (t: any) => t.ai_thread_id === createdThreadId
        );
        if (!exists) {
          const nowSeconds = Math.floor(Date.now() / 1000);
          const newThread = {
            ...payloadThread,
            user_id: payloadUserId,
            ai_thread_id: createdThreadId,
            pinned: payloadThread.pinned ?? false,
            messages: normalizeMessages(payloadThread.messages || []),
            updated_at: payloadThread.updated_at || { seconds: nowSeconds },
            created_at: payloadThread.created_at || { seconds: nowSeconds },
          };
          newState.userThreads = [newThread, ...(newState.userThreads || [])];
        }
      }
      // Reset thinking/loading state when thread is created
      newState.isThinking = false;
      newState.thinkingSteps = [];
      newState.currentThinkingStep = 0;
      return newState;
    }

    case ActionTypes.GET_USER_THREAD: {
      const userTurnActive = isActiveTurnInProgress(action.payload?.ai_thread?.active_turn);
      newState.isLoading = userTurnActive;
      newState.aiThreadId =
        action.payload?.ai_thread?.ai_thread_id || newState.aiThreadId;
      const incomingThreadIdUser = newState.aiThreadId;
      const userThreadCapacity = action.payload?.ai_thread?.automation_capacity;
      if (incomingThreadIdUser && (userThreadCapacity === 0 || userThreadCapacity === 3)) {
        newState.threadAutomationByThreadId = { ...state.threadAutomationByThreadId, [String(incomingThreadIdUser)]: userThreadCapacity };
      }
      const isSameThreadUser = sameThreadId(incomingThreadIdUser, state.aiThreadId);
      const hasStreamingUser = hasStreamingAssistant(state.messages);
      if (!(isSameThreadUser && hasStreamingUser)) {
        newState.messages = normalizeMessages(
          action.payload?.ai_thread?.messages || []
        );
      }
      syncRemoteShellFromThread(newState, action.payload?.ai_thread);
      if (userTurnActive || preserveInFlightThinking(state, incomingThreadIdUser)) {
        newState.isLoading = true;
        newState.isThinking = true;
      } else {
        newState.isThinking = false;
        newState.thinkingSteps = [];
        newState.currentThinkingStep = 0;
      }
      return newState;
    }

    case ActionTypes.ADD_MESSAGE: {
      const message = action.payload as ChatMessage;
      
      // Deduplicate by entry_id (Redis Stream entry ID: timestamp-sequence)
      // This handles events received multiple times (e.g., reconnection, replay, multiple tabs).
      if (
        message.entry_id &&
        (hasExistingEntryId(newState.messages, message) ||
          hasExistingEntryId(getSelectedThreadMessages(newState), message))
      ) {
        return state;
      }

      if (
        !message.pending &&
        !message.isStreaming &&
        !message.isComplete &&
        (hasExistingPersistedMessage(newState.messages, message) ||
          hasExistingPersistedMessage(getSelectedThreadMessages(newState), message))
      ) {
        return state;
      }
      
      // Handle streaming AI chunks. ai_chunk/stream_chunk payloads are delta-only by protocol.
      if (message.isStreaming && message.user_id === "neo") {
        if (
          hasReplayChunkForCompletedAssistantMessage(newState.messages, message) ||
          hasReplayChunkForCompletedAssistantMessage(getSelectedThreadMessages(newState), message)
        ) {
          return state;
        }

        const chunkContent = message.content ?? "";
        const lastIdx = newState.messages.length - 1;
        const lastMsg = lastIdx >= 0 ? newState.messages[lastIdx] : null;
        const isLastStreaming = lastMsg?.user_id === "neo" && lastMsg?.isStreaming === true;

        if (isLastStreaming && lastIdx >= 0) {
          const updated = {
            ...lastMsg,
            ...message,
            content: `${lastMsg?.content ?? ""}${chunkContent}`,
            stream_entry_ids: appendMessageEntryId(lastMsg, message),
          };
          newState.messages = [
            ...newState.messages.slice(0, lastIdx),
            updated,
          ];
        } else {
          // First chunk: create new message
          const chunkMessage = {
            ...message,
            content: chunkContent,
            stream_entry_ids: appendMessageEntryId(null, message),
          };
          newState.messages = [...newState.messages, chunkMessage];
        }

        if (newState.selectedUserThreadId) {
          const threadMessages = (newState.userThreads || []).find(
            (t: any) => sameThreadId(t.ai_thread_id, newState.selectedUserThreadId)
          )?.messages ?? [];
          const tLastIdx = threadMessages.length - 1;
          const tLast = tLastIdx >= 0 ? threadMessages[tLastIdx] : null;
          const tLastStreaming = tLast?.user_id === "neo" && tLast?.isStreaming === true;
          const chunkMessage = newState.messages[newState.messages.length - 1];
          newState.userThreads = (newState.userThreads || []).map((thread) => {
            if (!sameThreadId(thread.ai_thread_id, newState.selectedUserThreadId)) return thread;
            if (tLastStreaming && tLastIdx >= 0) {
              const updated = {
                ...tLast,
                ...message,
                content: `${tLast?.content ?? ""}${chunkContent}`,
                stream_entry_ids: appendMessageEntryId(tLast, message),
              };
              return {
                ...thread,
                messages: [...(thread.messages || []).slice(0, tLastIdx), updated],
              };
            }
            return { ...thread, messages: [...(thread.messages || []), chunkMessage] };
          });
        }
        return newState;
      }

      if (message.isComplete && message.user_id === "neo") {
        const lastIdx = newState.messages.length - 1;
        const lastMsg = lastIdx >= 0 ? newState.messages[lastIdx] : null;
        const isLastStreaming = lastMsg?.user_id === "neo" && lastMsg?.isStreaming === true;

        if (isLastStreaming && lastIdx >= 0) {
          const completed = {
            ...lastMsg,
            isStreaming: false,
            agent_display_name: message.agent_display_name ?? null,
            ...(typeof message.content === "string" && message.content.length > 0 ? { content: message.content } : {}),
            content_type: message.content_type || lastMsg.content_type || "MESSAGE",
            ...(message.message_id ? { message_id: message.message_id } : {}),
            ...(message.entry_id ? { entry_id: message.entry_id } : {}),
            stream_entry_ids: appendMessageEntryId(lastMsg, message),
          };
          newState.messages = [
            ...newState.messages.slice(0, lastIdx),
            completed,
          ];

          if (newState.selectedUserThreadId) {
            newState.userThreads = (newState.userThreads || []).map((thread) => {
              if (!sameThreadId(thread.ai_thread_id, newState.selectedUserThreadId)) return thread;
              const threadMessages = thread.messages || [];
              const tLastIdx = threadMessages.length - 1;
              const tLast = tLastIdx >= 0 ? threadMessages[tLastIdx] : null;
              if (tLast?.user_id === "neo" && tLast?.isStreaming === true) {
                return {
                  ...thread,
                  messages: [
                    ...threadMessages.slice(0, tLastIdx),
                    {
                      ...tLast,
                      isStreaming: false,
                      agent_display_name: message.agent_display_name ?? null,
                      ...(typeof message.content === "string" && message.content.length > 0 ? { content: message.content } : {}),
                      content_type: message.content_type || tLast.content_type || "MESSAGE",
                      ...(message.message_id ? { message_id: message.message_id } : {}),
                      ...(message.entry_id ? { entry_id: message.entry_id } : {}),
                      stream_entry_ids: appendMessageEntryId(tLast, message),
                    },
                  ],
                };
              }
              return thread;
            });
          }
          return newState;
        }

        if (
          hasExistingPersistedMessage(newState.messages, message) ||
          hasExistingPersistedMessage(getSelectedThreadMessages(newState), message)
        ) {
          return state;
        }
      }

      if (
        hasDuplicateCompletedAssistantMessage(newState.messages, message) ||
        hasDuplicateCompletedAssistantMessage(getSelectedThreadMessages(newState), message)
      ) {
        return state;
      }

      if (message.pending && message.content) {
        const existingPendingIndex = findPendingMessageIndex(newState.messages, message);
        
        if (existingPendingIndex !== -1) {
          const mergedMessage = mergePendingMessage(newState.messages[existingPendingIndex], message);
          newState.messages = newState.messages.map((m, idx) =>
            idx === existingPendingIndex ? mergedMessage : m
          );
          updateSelectedThreadMessages(newState, (threadMessages) => {
            const threadPendingIndex = findPendingMessageIndex(threadMessages, message);
            if (threadPendingIndex === -1) return [...threadMessages, mergedMessage];
            return threadMessages.map((m, idx) =>
              idx === threadPendingIndex ? mergePendingMessage(m, message) : m
            );
          });
          return newState;
        }
      }
      
      if (message.pending) {
        newState.messages = [...newState.messages, message];
        updateSelectedThreadMessages(newState, (threadMessages) => {
          const existingPendingIndex = findPendingMessageIndex(threadMessages, message);
          if (existingPendingIndex !== -1) {
            return threadMessages.map((m, idx) =>
              idx === existingPendingIndex ? mergePendingMessage(m, message) : m
            );
          }
          return [...threadMessages, message];
        });
        return newState;
      }
      
      // Replace pending optimistic message when server confirms it (prevents duplicate)
      if (message.user_id && message.user_id !== "neo" && !message.pending) {
        const pendingIndex = findPendingMessageIndex(newState.messages, message);
        if (pendingIndex !== -1) {
          newState.messages = [
            ...newState.messages.slice(0, pendingIndex),
            confirmPendingMessage(newState.messages[pendingIndex], message),
            ...newState.messages.slice(pendingIndex + 1),
          ];
          
          updateSelectedThreadMessages(newState, (threadMessages) => {
            const threadPendingIndex = findPendingMessageIndex(threadMessages, message);
            if (threadPendingIndex === -1) return [...threadMessages, { ...message, pending: false }];
            return [
              ...threadMessages.slice(0, threadPendingIndex),
              confirmPendingMessage(threadMessages[threadPendingIndex], message),
              ...threadMessages.slice(threadPendingIndex + 1),
            ];
          });
          return newState;
        }
      }
      
      
      // Match messages by message_id (real message_id, not temp)
      if (message.message_id && !String(message.message_id).startsWith('temp_')) {
        const incomingMessageId = String(message.message_id);
        const incomingHumanReview = extractHumanReviewPayloadFromContent(message.content);
        const existingMessageIndex = newState.messages.findIndex(
          (m) => m.message_id != null && String(m.message_id) === incomingMessageId
        );
        if (existingMessageIndex !== -1) {
          const existingMessage = newState.messages[existingMessageIndex] as any;
          const shouldKeepHumanReviewState =
            existingMessage?.user_id === "neo" &&
            existingMessage?.content_type === "HUMAN_REVIEW" &&
            message?.user_id === "neo" &&
            !message.human_review &&
            Boolean(incomingHumanReview);
          const mergedMessage = shouldKeepHumanReviewState
            ? {
                ...message,
                content: "",
                content_type: "HUMAN_REVIEW",
                human_review: existingMessage?.human_review || incomingHumanReview,
              }
            : message;
          newState.messages = newState.messages.map((m, idx) =>
            idx === existingMessageIndex ? { ...m, ...mergedMessage } : m
          );
          if (newState.selectedUserThreadId) {
            newState.userThreads = (newState.userThreads || []).map((thread) => {
              if (sameThreadId(thread.ai_thread_id, newState.selectedUserThreadId)) {
                const threadMessages = thread.messages || [];
                const existingThreadMessageIndex = threadMessages.findIndex(
                  (m) => m.message_id != null && String(m.message_id) === incomingMessageId
                );
                if (existingThreadMessageIndex !== -1) {
                  return {
                    ...thread,
                    messages: threadMessages.map((m, idx) =>
                      idx === existingThreadMessageIndex ? { ...m, ...mergedMessage } : m
                    ),
                  };
                }
                return { ...thread, messages: [...threadMessages, mergedMessage] };
              }
              return thread;
            });
          }
          return newState;
        }
      }
      
      newState.messages = [...newState.messages, message];
      
      updateSelectedThreadMessages(newState, (threadMessages) => [...threadMessages, message]);
      return newState;
    }


    case ActionTypes.UPDATE_MESSAGE_STATUS: {
      const { message_id, request_id, status, pending } = action.payload;
      const requestId = request_id; // Convert snake_case to camelCase
      
      // Update message by message_id or requestId (for pending messages)
      let messageIndex = -1;
      if (message_id) {
        messageIndex = newState.messages.findIndex(
          (m) => m.message_id != null && String(m.message_id) === String(message_id)
        );
      }
      if (messageIndex === -1 && requestId) {
        for (let i = newState.messages.length - 1; i >= 0; i--) {
          const m = newState.messages[i];
          if (getRequestId(m) === String(requestId)) {
            messageIndex = i;
            break;
          }
        }
      }
      
      const updates: Partial<ChatMessage> = {};
      if (status !== undefined) updates.status = status;
      if (status === undefined && pending === false) updates.status = "confirmed";
      if (pending !== undefined) updates.pending = pending;
      if (message_id) {
        updates.message_id = message_id;
      }

      if (messageIndex !== -1) {
        newState.messages = newState.messages.map((m, idx) =>
          idx === messageIndex ? { ...m, ...updates } : m
        );
      }

      updateSelectedThreadMessages(newState, (threadMessages) => {
        let threadMessageIndex = -1;
        if (message_id) {
          threadMessageIndex = threadMessages.findIndex(
            (m) => m.message_id != null && String(m.message_id) === String(message_id)
          );
        }
        if (threadMessageIndex === -1 && requestId) {
          for (let i = threadMessages.length - 1; i >= 0; i--) {
            const m = threadMessages[i];
            if (getRequestId(m) === String(requestId)) {
              threadMessageIndex = i;
              break;
            }
          }
        }
        if (threadMessageIndex === -1) return threadMessages;
        return threadMessages.map((m, idx) =>
          idx === threadMessageIndex ? { ...m, ...updates } : m
        );
      });
      
      return newState;
    }

    case ActionTypes.DROP_STREAMING_ASSISTANT: {
      const dropStoppedTurnAssistant = (threadMessages: any[]) => {
        const list = threadMessages || [];
        let lastHuman = -1;
        for (let i = 0; i < list.length; i += 1) {
          if (list[i]?.user_id !== "neo") lastHuman = i;
        }
        if (lastHuman < 0) {
          return list.filter((m) => !(m?.user_id === "neo" && m?.isStreaming === true));
        }
        return list.slice(0, lastHuman + 1);
      };
      newState.messages = dropStoppedTurnAssistant(newState.messages);
      updateSelectedThreadMessages(newState, dropStoppedTurnAssistant);
      return newState;
    }

    case ActionTypes.REMOVE_PENDING_MESSAGE: {
      const rawRequestId =
        action.payload?.request_id ?? action.payload?.requestId ?? "";
      const requestId = String(rawRequestId).trim();
      if (!requestId) return newState;

      const shouldRemove = (m: any) =>
        Boolean(m?.pending) && getRequestId(m) === requestId;

      newState.messages = (newState.messages || []).filter((m) => !shouldRemove(m));
      updateSelectedThreadMessages(newState, (threadMessages) =>
        (threadMessages || []).filter((m) => !shouldRemove(m))
      );
      return newState;
    }

    case ActionTypes.HUMAN_REVIEW_RESPONDED: {
      if (action.payload?.card_updates) {
        const update = (message: any) => updateWaitDeviceStatus(message, action.payload.card_updates);
        newState.messages = newState.messages.map(update);
        updateSelectedThreadMessages(newState, messages => messages.map(update));
        return newState;
      }
      const {
        status,
        pause_ids,
        information_answers,
        message_id,
        tool_action,
        call_id,
        plan_action,
        plan_id,
        selected_plan_name,
        schedule_id,
        schedule_title,
      } = action.payload || {};

      const toArray = (v: any) =>
        v === null || v === undefined ? [] : Array.isArray(v) ? v : [v];
      const statusCallIds = toArray(call_id).filter(Boolean).map((v: any) => String(v));
      const statusPlanIds = toArray(plan_id).filter(Boolean).map((v: any) => String(v));

      const isHitlMessage = (m: any) =>
        m?.content_type === "HUMAN_REVIEW" && m?.user_id === "neo";
      const matchesStatus = (m: any) => {
        if (!isHitlMessage(m)) return false;
        if (message_id && m?.message_id && String(m.message_id) === String(message_id)) {
          return true;
        }
        const hr = m?.human_review || {};
        if (pause_ids?.length && hr.information_request?.some((info: any) => pause_ids.some((id: any) => info.id === `pause:${id}`))) return true;
        const messageCallIds = toArray(hr.call_id).filter(Boolean).map((v: any) => String(v));
        const messagePlanIds = toArray(hr.plan_name).filter(Boolean).map((v: any) => String(v));
        if (statusCallIds.length > 0 && messageCallIds.some((id: string) => statusCallIds.includes(id))) {
          return true;
        }
        if (statusPlanIds.length > 0 && messagePlanIds.some((id: string) => statusPlanIds.includes(id))) {
          return true;
        }
        return false;
      };
      const withStatusApplied = (m: any) => {
        const nextHumanReview = {
          ...(m?.human_review || {}),
          ...(information_answers ? { information_answers: { ...m?.human_review?.information_answers, ...information_answers } } : {}),
          ...(tool_action !== undefined ? { tool_action } : {}),
          ...(call_id !== undefined ? { call_id } : {}),
          ...(plan_action !== undefined ? { plan_action } : {}),
          ...(plan_id !== undefined ? { plan_id } : {}),
          ...(selected_plan_name !== undefined ? { selected_plan_name } : {}),
          ...(schedule_id !== undefined ? { schedule_id } : {}),
          ...(schedule_title !== undefined ? { schedule_title } : {}),
        };
        return {
          ...m,
          human_review_status: status,
          human_review: nextHumanReview,
        };
      };

      let targetIndex = -1;
      for (let i = newState.messages.length - 1; i >= 0; i--) {
        if (matchesStatus(newState.messages[i])) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex === -1 && !pause_ids?.length) {
        for (let i = newState.messages.length - 1; i >= 0; i--) {
          if (isHitlMessage(newState.messages[i])) {
            targetIndex = i;
            break;
          }
        }
      }

      if (targetIndex !== -1) {
        newState.messages = [...(newState.messages || [])];
        newState.messages[targetIndex] = withStatusApplied(newState.messages[targetIndex]);
      }

      if (newState.selectedUserThreadId) {
        newState.userThreads = (newState.userThreads || []).map((thread: any) => {
          if (!sameThreadId(thread.ai_thread_id, newState.selectedUserThreadId)) return thread;
          const threadMessages = thread.messages || [];
          let threadIndex = -1;
          for (let i = threadMessages.length - 1; i >= 0; i--) {
            if (matchesStatus(threadMessages[i])) {
              threadIndex = i;
              break;
            }
          }
          if (threadIndex === -1 && !pause_ids?.length) {
            for (let i = threadMessages.length - 1; i >= 0; i--) {
              if (isHitlMessage(threadMessages[i])) {
                threadIndex = i;
                break;
              }
            }
          }
          if (threadIndex === -1) return thread;
          return {
            ...thread,
            messages: threadMessages.map((m: any, idx: number) =>
              idx === threadIndex ? withStatusApplied(m) : m
            ),
          };
        });
      }

      return newState;
    }

    case ActionTypes.MERGE_HITL_EDITED_ARGS: {
      const { messageId, userEdits } = action.payload || {};
      if (!messageId || !userEdits || typeof userEdits !== "object") return newState;

      const deepMerge = (base: any, override: any): any => {
        if (override == null) return base;
        if (base !== null && typeof base === "object" && !Array.isArray(base) && override !== null && typeof override === "object" && !Array.isArray(override)) {
          const result = { ...base };
          Object.keys(override).forEach((k) => {
            result[k] = deepMerge(result[k], override[k]);
          });
          return result;
        }
        return override;
      };

      const updateMessageTools = (msg: any) => {
        if (!msg?.human_review?.tools || !Array.isArray(msg.human_review.tools)) return msg;
        const tools = msg.human_review.tools.map((tool: any, index: number) => {
          const edits = userEdits[index];
          if (edits == null || typeof edits !== "object") return tool;
          let existing: any = {};
          try {
            const raw = tool.args_json;
            if (typeof raw === "string") existing = JSON.parse(raw || "{}");
            else if (raw && typeof raw === "object") existing = raw;
          } catch {
            existing = {};
          }
          const merged = deepMerge(existing, edits);
          return { ...tool, args_json: JSON.stringify(merged) };
        });
        return {
          ...msg,
          human_review: { ...msg.human_review, tools },
        };
      };

      const msgIndex = (newState.messages || []).findIndex(
        (m: any) => m?.message_id != null && String(m.message_id) === String(messageId)
      );
      if (msgIndex !== -1) {
        newState.messages = [...(newState.messages || [])];
        newState.messages[msgIndex] = updateMessageTools(newState.messages[msgIndex]);
      }

      if (newState.selectedUserThreadId && newState.userThreads) {
        newState.userThreads = newState.userThreads.map((thread: any) => {
          if (!sameThreadId(thread.ai_thread_id, newState.selectedUserThreadId)) return thread;
          const threadMessages = (thread.messages || []).map((m: any) =>
            m?.message_id != null && String(m.message_id) === String(messageId)
              ? updateMessageTools(m)
              : m
          );
          return { ...thread, messages: threadMessages };
        });
      }

      return newState;
    }

    case ActionTypes.FETCH_USER_THREADS_REQUEST:
      return state;

    case ActionTypes.FETCH_USER_THREADS_SUCCESS:
      // The list payload is metadata-only (messages: []). Preserve messages
      // already loaded via the detail fetch so a background list refresh never
      // wipes the open thread's transcript.
      const previousThreadsById = new Map(
        (state.userThreads || []).map((thread: any) => [String(thread?.ai_thread_id), thread])
      );
      const fetchedThreads = (action.payload?.ai_threads || []).map((thread: any) => {
        const incomingMessages = normalizeMessages(thread.messages || []);
        if (incomingMessages.length === 0) {
          const previous = previousThreadsById.get(String(thread?.ai_thread_id));
          const previousMessages = Array.isArray(previous?.messages) ? previous.messages : [];
          if (previousMessages.length > 0) {
            return { ...thread, messages: previousMessages };
          }
        }
        return {
          ...thread,
          messages: incomingMessages
        };
      });
      const selectedFromPrevious = (state.userThreads || []).find((thread: any) =>
        sameThreadId(thread.ai_thread_id, state.selectedUserThreadId)
      );
      const selectedInFetched = fetchedThreads.some((thread: any) =>
        sameThreadId(thread.ai_thread_id, state.selectedUserThreadId)
      );
      newState.userThreads =
        selectedFromPrevious && !selectedInFetched
          ? [selectedFromPrevious, ...fetchedThreads]
          : fetchedThreads;
      {
        // GetUserAiThreads only returns Chat Assistant user threads. Ticket and device
        // chats are never in that list — do not treat their absence as "thread deleted"
        // or we wipe messages + remoteShellByThreadId while a shell session is active.
        const userThreadIds = (newState.userThreads || []).map((thread: any) => thread.ai_thread_id);
        const selectedExists =
          newState.selectedUserThreadId == null ||
          userThreadIds.some((threadId: any) => sameThreadId(threadId, newState.selectedUserThreadId));
        if (!selectedExists) {
          newState.selectedUserThreadId = null;
        }
      }
      return newState;

    case ActionTypes.FETCH_USER_THREADS_FAILURE:
      newState.error = action.payload;
      return newState;

    case ActionTypes.SET_SELECTED_USER_THREAD_ID:
      if (
        (newState.selectedUserThreadId == null && action.payload == null) ||
        sameThreadId(newState.selectedUserThreadId, action.payload)
      ) {
        return state;
      }
      newState.selectedUserThreadId = action.payload;
      return newState;

    case ActionTypes.DELETE_USER_THREAD:
      newState.remoteShellByThreadId = deleteShellKey(
        cloneShellMap(newState.remoteShellByThreadId),
        action.payload
      );
      newState.computerUseByThreadId = deleteComputerUseKey(
        cloneComputerUseMap(newState.computerUseByThreadId),
        action.payload
      );
      newState.userThreads = (newState.userThreads || []).filter(
        (thread) => !sameThreadId(thread.ai_thread_id, action.payload)
      );
      if (sameThreadId(newState.selectedUserThreadId, action.payload)) {
          newState.selectedUserThreadId = null;
      }
      if (sameThreadId(newState.aiThreadId, action.payload)) {
        newState.aiThreadId = null;
        newState.messages = [];
        newState.isThinking = false;
        newState.thinkingSteps = [];
        newState.currentThinkingStep = 0;
        newState.isLoading = false;
      }
      return newState;

    case ActionTypes.RENAME_USER_THREAD:
      const { aiThreadId: renameThreadId, newTitle } = action.payload;
      newState.userThreads = (newState.userThreads || []).map((thread) =>
        sameThreadId(thread.ai_thread_id, renameThreadId)
          ? { ...thread, thread_name: { value: newTitle } }
          : thread
      );
      return newState;

    case ActionTypes.UPDATE_THREAD_PINNED:
      const { ai_thread_id: pinnedThreadId, pinned } = action.payload;
      newState.userThreads = (newState.userThreads || []).map((thread) =>
        thread.ai_thread_id === pinnedThreadId
          ? { ...thread, pinned }
          : thread
      );
      return newState;

    case ActionTypes.UPDATE_THREAD_SHARED:
      const { ai_thread_id: sharedThreadId, shared } = action.payload;
      newState.userThreads = (newState.userThreads || []).map((thread) =>
        sameThreadId(thread.ai_thread_id, sharedThreadId)
          ? { ...thread, shared }
          : thread
      );
      return newState;

    case ActionTypes.UPDATE_THREAD_NAME:
      const { ai_thread_id: nameThreadId, thread_name: name } = action.payload;
      const normalizedThreadName =
        typeof name === "string"
          ? { value: name }
          : name && typeof name === "object" && "value" in name
          ? name
          : name != null
          ? { value: String(name) }
          : null;
      newState.userThreads = (newState.userThreads || []).map((thread) =>
        thread.ai_thread_id === nameThreadId
          ? {
              ...thread,
              thread_name: normalizedThreadName,
            }
          : thread
      );
      return newState;

    case ActionTypes.UPSERT_USER_THREAD: {
      const incoming = action.payload || {};
      const incomingThreadId = incoming.ai_thread_id;
      if (incomingThreadId == null) return newState;

      const normalizedIncoming = {
        ...incoming,
        messages: normalizeMessages(incoming.messages || []),
      };
      const existingIndex = (newState.userThreads || []).findIndex((thread) =>
        sameThreadId(thread.ai_thread_id, incomingThreadId)
      );
      if (existingIndex === -1) {
        newState.userThreads = [normalizedIncoming, ...(newState.userThreads || [])];
      } else {
        // Keep local-only in-flight messages (pending sends, streaming chunks)
        // across a detail upsert: the fetched snapshot predates them and must
        // not wipe the live turn out of the pane.
        const existingMessages = (newState.userThreads || [])[existingIndex]?.messages || [];
        const localTail = existingMessages.filter(
          (m: any) => m?.pending || m?.isStreaming
        );
        const merged =
          localTail.length > 0
            ? [
                ...normalizedIncoming.messages,
                ...localTail.filter(
                  (local: any) =>
                    !normalizedIncoming.messages.some(
                      (m: any) =>
                        (m?.message_id && local?.message_id && m.message_id === local.message_id) ||
                        (m?.entry_id && local?.entry_id && m.entry_id === local.entry_id)
                    )
                ),
              ]
            : normalizedIncoming.messages;
        newState.userThreads = (newState.userThreads || []).map((thread, idx) =>
          idx === existingIndex ? { ...thread, ...normalizedIncoming, messages: merged } : thread
        );
      }
      syncRemoteShellFromThread(newState, normalizedIncoming);
      return newState;
    }

    case ActionTypes.HANDLE_STATUS_UPDATE:
      // Trust upstream: server filters updates (Redis stream check). Just append.
      if (!newState.isThinking) {
        newState.isThinking = true;
        newState.thinkingSteps = [];
        newState.currentThinkingStep = 0;
      }
      newState.isLoading = true;
      newState.thinkingSteps = [...(newState.thinkingSteps || []), action.payload];
      newState.currentThinkingStep = newState.thinkingSteps.length;
      return newState;

    case ActionTypes.START_THINKING:
      if (!newState.isThinking) {
        newState.thinkingSteps = [];
        newState.currentThinkingStep = 0;
      }
      newState.isThinking = true;
      newState.isLoading = true;
      return newState;

    case ActionTypes.UPDATE_THINKING_STEP:
      newState.currentThinkingStep = action.payload;
      return newState;

    case ActionTypes.END_THINKING:
      if (!newState.isThinking && (!newState.thinkingSteps || newState.thinkingSteps.length === 0)) {
        return newState;
      }
      newState.isThinking = false;
      newState.thinkingSteps = [];
      newState.currentThinkingStep = 0;
      return newState;

    case ActionTypes.SET_PLAN_PROGRESS:
      newState.planProgress = action.payload;
      return newState;

    case ActionTypes.CLEAR_PLAN_PROGRESS:
      newState.planProgress = null;
      return newState;

    case ActionTypes.SET_REMOTE_SESSION: {
      const p = action.payload as {
        threadId?: string | number;
        remoteSession?: any;
      };
      const threadId =
        p?.threadId != null ? String(p.threadId) : null;
      if (!threadId) return state;
      const map = cloneShellMap(newState.remoteShellByThreadId);
      const prev = map[threadId] || emptyShellSlot();
      map[threadId] = {
        ...prev,
        remoteSession: p.remoteSession ?? null,
      };
      newState.remoteShellByThreadId = map;
      return newState;
    }

    case ActionTypes.ADD_SSH_EVENT: {
      const p = action.payload as { threadId?: string | number; event?: any };
      const threadId =
        p?.threadId != null ? String(p.threadId) : null;
      const event = p?.event !== undefined ? p.event : p;
      if (!threadId || event == null || typeof event !== "object") return state;
      const map = cloneShellMap(newState.remoteShellByThreadId);
      const prev = map[threadId] || emptyShellSlot();
      const sshEvents = [...(prev.sshEvents || []), event];
      const capped =
        sshEvents.length > 5000 ? sshEvents.slice(-5000) : sshEvents;
      map[threadId] = { ...prev, sshEvents: capped };
      newState.remoteShellByThreadId = map;
      return newState;
    }

    case ActionTypes.CLEAR_REMOTE_SESSION: {
      const tid = (action.payload as { threadId?: string | number } | undefined)
        ?.threadId;
      if (tid != null) {
        newState.remoteShellByThreadId = deleteShellKey(
          cloneShellMap(newState.remoteShellByThreadId),
          tid
        );
      } else {
        newState.remoteShellByThreadId = {};
      }
      return newState;
    }

    case ActionTypes.APPLY_COMPUTER_USE_EVENT: {
      const p = action.payload as { threadId?: string | number; event?: any };
      const threadId = p?.threadId != null ? String(p.threadId) : null;
      if (!threadId) return state;

      const map = cloneComputerUseMap(newState.computerUseByThreadId);
      const runs = { ...(map[threadId]?.runs || {}) };
      const runKey = resolveComputerUseRunKey(runs, p?.event);
      if (!runKey) return state;
      runs[runKey] = applyComputerUseEvent(runs[runKey] || emptyRun(runKey, p.event), p.event);
      map[threadId] = { runs };
      newState.computerUseByThreadId = map;
      return newState;
    }

    case ActionTypes.SET_COMPUTER_USE_RUNS: {
      // Seeded from the stored rows on a cold load. Anything the live stream has already put
      // in place wins: it is newer than a fetch that was in flight while the run moved on.
      const p = action.payload as { threadId?: string | number; runs?: any[] };
      const threadId = p?.threadId != null ? String(p.threadId) : null;
      if (!threadId) return state;

      const map = cloneComputerUseMap(newState.computerUseByThreadId);
      const existing = map[threadId]?.runs || {};
      const runs: Record<string, ComputerUseRunState> = {};

      for (const run of p.runs || []) {
        const key = computerUseRunKey({ call_id: run?.call_id, job_id: run?.job_id });
        if (!key) continue;
        // Stamped with the job that produced them before anything else looks at them, because
        // that job is about to stop being recoverable: several jobs share one key.
        const stored = (Array.isArray(run?.recordings) ? run.recordings : []).map((rec: any) => ({
          ...rec,
          job_id: run?.job_id != null ? Number(run.job_id) : null,
        }));

        // ACCUMULATED across jobs, not overwritten by the last one.
        //
        // A run is keyed by the tool call, and one tool call spans several jobs once a HITL pause
        // is resumed under it -- the answer, the retry, the work that follows. This loop assigned
        // ``runs[key]`` per job, so on a reload every job but the last was thrown away and its
        // recording with it. Live the panel had them all, which is why they appeared to vanish
        // only on refresh.
        const alreadySeen = runs[key] || existing[key];
        runs[key] = alreadySeen
          ? { ...alreadySeen, recordings: refreshRecordingLinks(alreadySeen.recordings, stored) }
          : {
              jobId: run?.job_id != null ? Number(run.job_id) : null,
              callId: String(run?.call_id || key),
              sessionId: run?.session_id ? String(run.session_id) : null,
              status: String(run?.status || "completed"),
              browserUrl: run?.browser_url ? String(run.browser_url) : null,
              // A run rebuilt after a reload has usually been going for a while, so the
              // stored rows answer this too -- otherwise the panel would sit on "starting"
              // in front of a browser that is already working.
              browserReady: Boolean(run?.browser_ready),
              recordings: stored,
              startedAt: run?.started_at ? String(run.started_at) : null,
              // The live browser's own start, which the stored rows carry separately: a
              // rescued run's second browser opened long after the job did, and placing it at
              // the job's time would draw it above the messages that led to it.
              sessionStartedAt:
                run?.browser_started_at && run?.session_id ? { [String(run.session_id)]: String(run.browser_started_at) } : {},
              needsTechnician: Boolean(run?.needs_technician),
              recordingError: null,
              recordingSkipped: Boolean(run?.recording_skipped),
              readOnly: Boolean(run?.read_only),
              silent: Boolean(run?.silent),
              title: String(run?.title || ""),
              // A run rebuilt from stored rows is not mid-try: the label it drives only shows
              // while the browser is live, and this one's has long since closed.
              retry: null,
            };
      }

      // Runs already on screen but absent from this answer keep their place; the fetch may
      // simply predate them.
      map[threadId] = { runs: { ...existing, ...runs } };
      newState.computerUseByThreadId = map;
      return newState;
    }

    case ActionTypes.CLEAR_CHAT:
      newState.messages = [];
      newState.aiThreadId = null;
      newState.ticketId = null;
      newState.selectedUserThreadId = null;
      newState.planProgress = null;
      newState.remoteShellByThreadId = {};
      newState.computerUseByThreadId = {};
      return newState;

    case ActionTypes.CLEAR_MESSAGES:
      newState.messages = [];
      newState.planProgress = null;
      // Reset thinking/loading state when messages are cleared
      newState.isThinking = false;
      newState.thinkingSteps = [];
      newState.currentThinkingStep = 0;
      newState.remoteShellByThreadId = {};
      newState.computerUseByThreadId = {};
      return newState;

    default:
      return state;
  }
};

export default reducer;
